import { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from '../../redux/store';
import useAuth from 'src/hooks/useAuth';
import useTabs from 'src/hooks/useTabs';

// Actions
import actionDescontinuarAgendamento from 'src/actions/agendamentos/actionDescontinuarAgendamento';
import actionEditarAgendamento from 'src/actions/agendamentos/actionEditarAgendamento';
import actionListaAgendamentos from 'src/actions/agendamentos/actionListaCampanhas';
import v2_actionCriarAgendamento from 'src/actions/v2/agendamentos/v2_actionCriarAgendamento';
import actionListaCanaisPorCliente from 'src/actions/clientes/actionListaCanaisPorCliente';
import v1_actionWboTemplates from "src/actions/v1/meta/wbo/v1_actionWboTemplates";
import v2_actionUploadS3 from 'src/actions/v2/global/actionUploadS3';
import { getListaOptionsFunil } from 'src/redux/slices/global';

// Components
import { MENSAGENS_AGENDADAS_DESIGN } from './MensagensAgendadas.design';
import { cn } from 'src/@/lib/utils';
import { fDateTimeSuffix } from 'src/utils/formatTime';
import MicRecorder from "mic-recorder-to-mp3";

// UI
import { Button } from 'src/@/components/ui/button';
import { Input } from 'src/@/components/ui/input';
import { Textarea } from 'src/@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'src/@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from 'src/@/components/ui/dialog';
import { Switch } from 'src/@/components/ui/switch';
import { Badge } from 'src/@/components/ui/badge';
import { ScrollArea } from 'src/@/components/ui/scroll-area';
import { toast } from 'src/@/components/ui/use-toast';
import { Tabs, TabsList, TabsTrigger } from 'src/@/components/ui/tabs';
import { RadioGroup, RadioGroupItem } from 'src/@/components/ui/radio-group';
import { Label } from 'src/@/components/ui/label';
import { Pagination, PaginationContent, PaginationItem, PaginationNext, PaginationPrevious } from 'src/@/components/ui/pagination';

// Icons
import Iconify from 'src/components/Iconify';
import { Trash2, Edit2, Search, Plus, CalendarClock, AlertCircle } from 'lucide-react';
import { BsMicFill } from 'react-icons/bs';
import { FaCheck } from 'react-icons/fa';

const STATUS_OPTIONS = [
    { value: 'all', label: "Todos" },
    { value: 'agendado', label: "Agendados" },
    { value: 'em andamento', label: "Em Andamento" },
    { value: 'finalizado', label: "Finalizado" },
    { value: 'erro', label: "Falhados" }
];

const TABLE_HEAD = [
    { id: 'numero', label: 'Contato', width: '20%' },
    { id: 'conteudo', label: 'Conteúdo', width: '25%' },
    { id: 'status_envio', label: 'Status Envio', width: '15%' },
    { id: 'nome_usuario', label: 'Responsável', width: '15%' },
    { id: 'data_envio', label: 'Data Envio', width: '15%' },
    { id: 'ativo', label: 'Ativo', width: '5%' },
    { id: 'actions', label: '', width: '5%' },
];

const STEPS = [
    { id: 1, label: "Destinatário", subtitle: "Quem receberá?" },
    { id: 2, label: "Conteúdo", subtitle: "Defina a mensagem" },
    { id: 3, label: "Agendamento", subtitle: "Data e Hora" }
];

export default function MensagensAgendadas() {
    const dispatch = useDispatch();
    const { cliente, user } = useAuth();
    const { currentTab: filterStatus, onChangeTab: onChangeFilterStatus } = useTabs('all');

    // --- State ---
    const [tableData, setTableData] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [filterName, setFilterName] = useState('');

    // Config
    const [clienteIdPadrao, setClienteIdPadrao] = useState(cliente.cliente_id_padrao || cliente._id);
    const [listaClientesCanais, setListaClientesCanais] = useState([]);
    const isMultisession = cliente.multiSession === 1;

    // Wizard / Dialog Modal State
    const [dialogOpen, setDialogOpen] = useState(false);
    const [activeStep, setActiveStep] = useState(1);
    const [isEdit, setIsEdit] = useState(false);
    const [agendamentoId, setAgendamentoId] = useState('');
    const [modalConfirmarExclusao, setModalConfirmarExclusao] = useState(false);
    const [loadingSubmit, setLoadingSubmit] = useState(false);

    // Form Fields
    const [numero, setNumero] = useState('');
    const [selectedClienteId, setSelectedClienteId] = useState('');
    const [isChatAtendimento, setIsChatAtendimento] = useState(false);

    // Content State
    const [contentType, setContentType] = useState('text'); // 'text', 'file', 'audio', 'template'
    const [body, setBody] = useState('');

    // Files & Audio
    const [files, setFiles] = useState([]);
    const [audioGravado, setAudioGravado] = useState(null);
    const [recording, setRecording] = useState(false);
    const [loadingAudio, setLoadingAudio] = useState(false);
    const recorder = useMemo(() => new MicRecorder({ bitRate: 128 }), []);

    // Templates
    const [templates, setTemplates] = useState([]);
    const [loadingTemplates, setLoadingTemplates] = useState(false);
    const [templateSelecionado, setTemplateSelecionado] = useState(null);
    const [templateVariaveis, setTemplateVariaveis] = useState({});
    const [useTemplate, setUseTemplate] = useState(false);

    // Schedule
    const [dataAgendamento, setDataAgendamento] = useState(new Date().toISOString().split('T')[0]);
    const [hora, setHora] = useState('');
    const [minuto, setMinuto] = useState('');

    // Derived
    const isWhatsAppBusiness = useMemo(() => {
        const channel = listaClientesCanais.find(c => (c.cliente_id === selectedClienteId || c.clienteId === selectedClienteId));
        return channel?.platform === 'whatsapp_business';
    }, [selectedClienteId, listaClientesCanais]);

    // --- Effects ---

    useEffect(() => {
        dispatch(getListaOptionsFunil(user.clienteId));
        if (isMultisession) {
            buscarListaClientesCanais();
        } else {
            callListaAgendamentos(cliente._id);
            // Se não for multisession, setar cliente atual
            setSelectedClienteId(cliente._id);
        }
    }, []);

    useEffect(() => {
        // Fetch templates if channel changes and is WABA
        const fetchTemplates = async () => {
            const currentChannel = listaClientesCanais.find(c => (c.cliente_id === selectedClienteId || c.clienteId === selectedClienteId));

            if (isWhatsAppBusiness && currentChannel) {
                setLoadingTemplates(true);
                try {
                    const idForTemplates = currentChannel.cliente_id || currentChannel.clienteId;
                    console.log('selectedClienteId', selectedClienteId, 'idForTemplates', idForTemplates);
                    const res = await v1_actionWboTemplates(idForTemplates);
                    setTemplates(res.data?.data?.templates || []);
                } catch (error) {
                    console.log('Erro ao buscar templates', error);
                } finally {
                    setLoadingTemplates(false);
                }
            }
        };
        fetchTemplates();
    }, [isWhatsAppBusiness, selectedClienteId, listaClientesCanais, clienteIdPadrao]);

    const handleChangeCanal = (val) => {
        console.log('handleChangeCanal', val);
        setClienteIdPadrao(val);
        setSelectedClienteId(val);
        callListaAgendamentos(val);
    }

    // --- Actions ---

    const handleToggleStatus = async (row, checked) => {
        try {
            await actionEditarAgendamento(row._id, { ...row, status: checked }); // Assuming simple update, check backend requirements if specific action needed
            setTableData(prev => prev.map(item => item._id === row._id ? { ...item, status: checked } : item));
            toast({ title: "Sucesso", description: `Agendamento ${checked ? 'ativado' : 'desativado'} com sucesso.` });
        } catch (error) {
            console.error(error);
            toast({ title: "Erro", description: "Erro ao atualizar status.", variant: "destructive" });
        }
    }

    const callListaAgendamentos = async (clienteId) => {
        try {
            const res = await actionListaAgendamentos(clienteId, user.id);
            setTableData(res.data.data);
        } catch (error) {
            console.log('Err', error);
            // toast({ title: "Erro", description: "Falha ao listar agendamentos", variant: "destructive" });
        }
    }

    const buscarListaClientesCanais = async () => {
        try {
            const res = await actionListaCanaisPorCliente(cliente._id);
            const canais = res.data.data || [];
            setListaClientesCanais(canais);

            if (canais.length > 0) {
                // Ensure we have a valid unique ID selected
                let channelToFetch = clienteIdPadrao;

                // If default is generic account ID, or not found in list, default to first channel's ID
                const exists = canais.find(c => (c.cliente_id === channelToFetch || c.clienteId === channelToFetch));

                if (!channelToFetch || channelToFetch === cliente._id || !exists) {
                    channelToFetch = canais[0].cliente_id || canais[0].clienteId;
                    setClienteIdPadrao(channelToFetch);
                }

                callListaAgendamentos(channelToFetch);

                // Sync selectedClienteId
                if (!selectedClienteId || selectedClienteId !== channelToFetch) {
                    setSelectedClienteId(channelToFetch);
                }
            }
        } catch (err) {
            console.log(err)
        }
    }

    // --- Helper Functions ---

    const limparCampos = () => {
        setActiveStep(1);
        setNumero('');
        setIsChatAtendimento(false);
        setContentType('text');
        setBody('');
        setFiles([]);
        setAudioGravado(null);
        setTemplateSelecionado(null);
        setTemplateVariaveis({});
        setUseTemplate(false);
        setDataAgendamento(new Date().toISOString().split('T')[0]);
        setHora('');
        setMinuto('');
        setAgendamentoId('');
        setIsEdit(false);
        if (isMultisession && listaClientesCanais.length > 0) {
            setSelectedClienteId(listaClientesCanais[0].cliente_id);
        }
    };

    const normalizarComponents = (components) => {
        if (!Array.isArray(components)) return [];
        return components.map((c) => ({
            ...c,
            type: typeof c.type === 'string' ? c.type.toUpperCase() : c.type,
        }));
    };

    const extrairVariaveisTemplate = (components) => {
        const variaveis = {};
        if (!components || !Array.isArray(components)) return variaveis;
        components.forEach((component) => {
            if (component.text) {
                const matches = component.text.match(/\{\{(\d+)\}\}/g);
                if (matches) {
                    matches.forEach((match) => {
                        const numero = match.replace(/\{\{|\}\}/g, '');
                        variaveis[numero] = '';
                    });
                }
            }
        });
        return variaveis;
    };

    const handleSelecionarTemplate = (templateId) => {
        const template = templates.find(t => (t.templateId || t._id) === templateId);
        if (!template) {
            setTemplateSelecionado(null);
            return;
        }
        const comps = normalizarComponents(template?.components || []);
        const conteudo = comps?.find(c => c.type === 'BODY')?.text || '';
        const variaveis = extrairVariaveisTemplate(comps);

        setTemplateSelecionado({
            templateId: template.templateId || template._id,
            name: template.name || template.templateName,
            conteudo: conteudo,
            components: comps
        });
        setTemplateVariaveis(variaveis);
    };

    const handleStartRecording = async () => {
        setLoadingAudio(true);
        try {
            await navigator.mediaDevices.getUserMedia({ audio: true });
            await recorder.start();
            setRecording(true);
        } catch (err) {
            console.log(err);
        } finally {
            setLoadingAudio(false);
        }
    };

    const handleStopRecording = async () => {
        try {
            const [buffer, blob] = await recorder.stop().getMp3();
            const file = new File(buffer, "audio.mp3", { type: blob.type, lastModified: Date.now() });
            setAudioGravado(file);
            setRecording(false);
        } catch (error) {
            console.log(error);
        }
    };

    const uploadFiles = async () => {
        const uploaded = [];
        // Normal Files
        for (let file of files) {
            const formData = new FormData();
            formData.append("file", file);
            try {
                const res = await v2_actionUploadS3(formData, `?path=plataforma/agendamentos`);
                if (!res.data.error) {
                    uploaded.push({ key: res.data.data.key, type: file.type });
                }
            } catch (e) {
                console.log(e);
            }
        }
        // Audio
        if (audioGravado) {
            const formData = new FormData();
            formData.append("file", audioGravado);
            try {
                const res = await v2_actionUploadS3(formData, `?path=plataforma/agendamentos`);
                if (!res.data.error) {
                    uploaded.push({ key: res.data.data.key, type: audioGravado.type, tipo: 'audio' });
                }
            } catch (e) {
                console.log(e);
            }
        }
        return uploaded;
    }

    // --- Wizards Handlers ---

    const handleNext = () => {
        if (activeStep === 1) {
            if (!numero) return toast({ title: "Erro", description: "Informe o número do contato", variant: "destructive" });
            if (isMultisession && !selectedClienteId) return toast({ title: "Erro", description: "Selecione o canal de envio", variant: "destructive" });
        }
        if (activeStep === 2) {
            if (contentType === 'text' && !body && !useTemplate) return toast({ title: "Erro", description: "Digite a mensagem ou selecione um template.", variant: "destructive" });
            if (useTemplate && !templateSelecionado) return toast({ title: "Erro", description: "Selecione um template.", variant: "destructive" });
            if ((contentType === 'file' && files.length === 0) && (contentType === 'audio' && !audioGravado) && !useTemplate) return toast({ title: "Erro", description: "Anexe um arquivo ou grave um áudio.", variant: "destructive" });
        }
        setActiveStep(prev => prev + 1);
    };

    const handleBack = () => setActiveStep(prev => prev - 1);

    const onSubmit = async () => {
        if (!hora || !minuto) return toast({ title: "Erro", description: "Defina o horário.", variant: "destructive" });

        setLoadingSubmit(true);
        try {
            let uploadedFiles = [];
            if (contentType === 'file' || contentType === 'audio') {
                uploadedFiles = await uploadFiles();
            }

            const payload = {
                dataAgendamento,
                hora,
                minuto,
                numero: numero.replace(/\D/g, ''), // Clean number
                texto: contentType === 'text' ? body : '',
                // Map fields based on contentType
                isFile: (contentType === 'file' || contentType === 'audio'),
                files: uploadedFiles,
                isChatAtendimento,

                // Template fields
                useTemplate: useTemplate,
                templateSelecionado: useTemplate ? templateSelecionado : null,
                templateVariaveis: useTemplate ? templateVariaveis : null,

                // Legacy/Generic fields for consistency
                scope: {
                    phone: numero,
                    dateSchedule: dataAgendamento,
                    hour: hora,
                    minute: minuto,
                    status_envio: "agendado",
                    cliente_id: selectedClienteId,
                    files: uploadedFiles,
                    useTemplate: useTemplate,
                    templateSelecionado: useTemplate ? templateSelecionado : null,
                    templateVariaveis: useTemplate ? templateVariaveis : null,
                    isChatAtendimento: isChatAtendimento
                }
            };

            // Handling Create vs Edit with slightly different Actions/Payloads
            if (isEdit) {
                const editPayload = {
                    action: (contentType === 'file' || contentType === 'audio') ? 'edit_info_with_file' : 'edit_info_without_file',
                    dataAgendamento: new Date(`${dataAgendamento}T${hora}:${minuto}:00`),
                    numero: numero.replace(/\D/g, ''),
                    hora,
                    minuto,
                    texto: body,
                    isFile: (contentType === 'file' || contentType === 'audio'),
                    files: uploadedFiles.length > 0 ? uploadedFiles : [], // Keep existing if not handled? logic simplification here
                    isChatAtendimento,
                    // Template? Assuming backend supports it if added to payload
                    useTemplate: useTemplate,
                    templateSelecionado: useTemplate ? templateSelecionado : null,
                    templateVariaveis: useTemplate ? templateVariaveis : null
                };
                await actionEditarAgendamento(agendamentoId, editPayload);
                toast({ title: "Sucesso", description: "Agendamento atualizado." });
            } else {
                await v2_actionCriarAgendamento(selectedClienteId, payload);
                toast({ title: "Sucesso", description: "Agendamento criado." });
            }

            setDialogOpen(false);
            callListaAgendamentos(isMultisession ? clienteIdPadrao : cliente._id);
            limparCampos();

        } catch (error) {
            console.log(error);
            toast({ title: "Erro", description: "Falha ao salvar agendamento.", variant: "destructive" });
        } finally {
            setLoadingSubmit(false);
        }
    };


    const handleOpenEdit = (row) => {
        limparCampos();
        setIsEdit(true);
        setAgendamentoId(row._id);
        setNumero(row.numero);
        setSelectedClienteId(row.cliente_id || (isMultisession ? clienteIdPadrao : cliente._id));
        setIsChatAtendimento(row.isChatAtendimento);

        // Date parsing
        if (row.data_envio) {
            const d = new Date(row.data_envio);
            setDataAgendamento(d.toISOString().split('T')[0]);
            setHora(d.getHours().toString().padStart(2, '0'));
            setMinuto(d.getMinutes().toString().padStart(2, '0'));
        }

        // Content parsing
        const config = row.config || {};
        const useTpl = config.useTemplate || row.useTemplate;

        if (useTpl) {
            setUseTemplate(true);
            const tplSelected = config.templateSelecionado || row.templateSelecionado;
            if (tplSelected) {
                setTemplateSelecionado(tplSelected);
            }
            const tplVars = config.templateVariaveis || row.templateVariaveis || {};
            setTemplateVariaveis(tplVars);
            setContentType('text');
        } else {
            setUseTemplate(false);
            if (row.file || (row.files && row.files.length > 0)) {
                // Check if audio
                const isAudio = row.files?.some(f => f.tipo === 'audio' || f.type?.includes('audio'));
                setContentType(isAudio ? 'audio' : 'file');
            } else {
                setContentType('text');
                setBody(row.texto || '');
            }
        }

        setDialogOpen(true);
    };

    const handleOpenDelete = (id) => {
        setAgendamentoId(id);
        setModalConfirmarExclusao(true);
    };

    const confirmDelete = async () => {
        try {
            await actionDescontinuarAgendamento(agendamentoId);
            toast({ title: "Sucesso", description: "Agendamento excluído." });
            callListaAgendamentos(isMultisession ? clienteIdPadrao : cliente._id);
        } catch (error) {
            toast({ title: "Erro", description: "Erro ao excluir.", variant: "destructive" });
        } finally {
            setModalConfirmarExclusao(false);
        }
    };

    // --- Render ---

    const dataFiltered = tableData.filter((item) => {
        if (filterStatus !== 'all' && item.status_envio !== filterStatus) return false;
        if (filterName) {
            const search = filterName.toLowerCase();
            return (
                (item.nome_usuario && item.nome_usuario.toLowerCase().includes(search)) ||
                (item.numero && item.numero.includes(search))
            );
        }
        return true;
    });

    return (
        <div className={MENSAGENS_AGENDADAS_DESIGN.page.container}>
            {/* Header */}
            <div className={MENSAGENS_AGENDADAS_DESIGN.header.container}>
                <div className={MENSAGENS_AGENDADAS_DESIGN.header.wrapper}>
                    <div className={MENSAGENS_AGENDADAS_DESIGN.header.left}>
                        <div className="flex items-center gap-2">
                            <div className={MENSAGENS_AGENDADAS_DESIGN.header.iconContainer}>
                                <CalendarClock className={MENSAGENS_AGENDADAS_DESIGN.header.icon} size={20} />
                            </div>
                            <div>
                                <h1 className={MENSAGENS_AGENDADAS_DESIGN.header.title}>Agendamentos</h1>
                                <p className={MENSAGENS_AGENDADAS_DESIGN.header.subtitle}>Gerencie suas mensagens programadas</p>
                            </div>
                        </div>
                        <div className={MENSAGENS_AGENDADAS_DESIGN.header.divider} />
                        <div className={MENSAGENS_AGENDADAS_DESIGN.filters.searchContainer}>
                            <div className={MENSAGENS_AGENDADAS_DESIGN.filters.searchIcon}>
                                <Search size={16} />
                            </div>
                            <Input
                                placeholder="Buscar..."
                                value={filterName}
                                onChange={(e) => setFilterName(e.target.value)}
                                className={MENSAGENS_AGENDADAS_DESIGN.filters.input}
                            />
                        </div>
                        <Tabs value={filterStatus === null ? 'all' : filterStatus} onValueChange={(v) => onChangeFilterStatus(null, v)} className="w-[400px]">
                            <TabsList className="grid w-full grid-cols-5 h-9 bg-gray-100/50 p-1">
                                {STATUS_OPTIONS.map((tab) => (
                                    <TabsTrigger key={tab.value} value={tab.value} className="text-[10px] px-2 h-7">{tab.label}</TabsTrigger>
                                ))}
                            </TabsList>
                        </Tabs>
                    </div>

                    <div className={MENSAGENS_AGENDADAS_DESIGN.header.right}>
                        {isMultisession && (
                            <Select value={clienteIdPadrao} onValueChange={value => handleChangeCanal(value)}>
                                <SelectTrigger className="h-9 w-[200px] bg-white">
                                    <SelectValue placeholder="Canal" />
                                </SelectTrigger>
                                <SelectContent>
                                    {listaClientesCanais.map((op) => {
                                        const val = op.cliente_id || op.clienteId;
                                        return <SelectItem key={val} value={val}>{op.nome}</SelectItem>
                                    })}
                                </SelectContent>
                            </Select>
                        )}
                        <Button onClick={() => { limparCampos(); setDialogOpen(true); }} className="bg-[#7C3AED] hover:bg-[#6D28D9] shadow-md h-9">
                            <Plus size={16} className="mr-2" /> Novo Agendamento
                        </Button>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className={MENSAGENS_AGENDADAS_DESIGN.page.contentWrapper}>
                <div className={MENSAGENS_AGENDADAS_DESIGN.page.card}>
                    <ScrollArea className="h-full">
                        <Table>
                            <TableHeader className="bg-gray-50/80 sticky top-0 z-10 backdrop-blur-sm">
                                <TableRow>
                                    {TABLE_HEAD.map(h => (
                                        <TableHead
                                            key={h.id}
                                            className="uppercase text-xs font-semibold text-gray-500 tracking-wider h-12"
                                            style={{ width: h.width }}
                                        >
                                            {h.label}
                                        </TableHead>
                                    ))}
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {dataFiltered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
                                    <TableRow key={row._id} className="hover:bg-gray-50/50 transition-colors border-b border-gray-100 last:border-0">
                                        <TableCell className="p-4 align-middle"><strong className="text-gray-700 font-medium">{row.numero}</strong></TableCell>
                                        <TableCell className="p-4 align-middle">
                                            {row.config?.useTemplate || row.useTemplate ? (
                                                <div className="flex flex-col">
                                                    <span className="text-xs font-semibold text-violet-600 bg-violet-50 px-2 py-0.5 rounded w-fit mb-1">
                                                        Template: {row.config?.templateSelecionado?.name || row.templateSelecionado?.name || 'Sem nome'}
                                                    </span>
                                                    <span className="text-sm text-gray-600 truncate max-w-[200px]" title={row.config?.templateSelecionado?.conteudo || row.templateSelecionado?.conteudo}>
                                                        {row.config?.templateSelecionado?.conteudo || row.templateSelecionado?.conteudo || 'Sem conteúdo'}
                                                    </span>
                                                </div>
                                            ) : (
                                                <span className="text-sm text-gray-600 truncate max-w-[200px]" title={row.texto}>
                                                    {row.texto || (row.files?.length > 0 ? `${row.files.length} arquivo(s)` : '-')}
                                                </span>
                                            )}
                                        </TableCell>
                                        <TableCell className="p-4 align-middle">
                                            <Badge variant="secondary" className={cn(
                                                "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border-0",
                                                row.status_envio === 'agendado' && "bg-blue-100 text-blue-800",
                                                row.status_envio === 'finalizado' && "bg-green-100 text-green-800",
                                                row.status_envio === 'erro' && "bg-red-100 text-red-800",
                                                row.status_envio === 'cancelado' && "bg-gray-100 text-gray-800"
                                            )}>{row.status_envio.toUpperCase()}</Badge>
                                        </TableCell>
                                        <TableCell className="p-4 align-middle text-gray-600">{row.nome_usuario || '-'}</TableCell>
                                        <TableCell className="p-4 align-middle text-gray-600 font-mono text-xs">{fDateTimeSuffix(new Date(row.data_envio))}</TableCell>
                                        <TableCell className="p-4 align-middle">
                                            <Switch
                                                checked={row.status}
                                                onCheckedChange={(checked) => handleToggleStatus(row, checked)}
                                            />
                                        </TableCell>
                                        <TableCell className="p-4 align-middle">
                                            <div className="flex justify-end gap-1">
                                                <Button variant="ghost" size="icon" className="h-8 w-8 hover:bg-indigo-50 hover:text-indigo-600" onClick={() => handleOpenEdit(row)}><Edit2 size={14} /></Button>
                                                <Button variant="ghost" size="icon" className="h-8 w-8 hover:bg-red-50 hover:text-red-600" onClick={() => handleOpenDelete(row._id)}><Trash2 size={14} /></Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {dataFiltered.length === 0 && <TableRow><TableCell colSpan={6} className="h-24 text-center text-gray-400">Nenhum agendamento encontrado.</TableCell></TableRow>}
                            </TableBody>
                        </Table>
                    </ScrollArea>

                    {/* Pagination */}
                    <div className="flex items-center justify-between px-4 py-4 border-t border-gray-100 bg-white">
                        <div className="flex items-center space-x-2 text-sm text-gray-500">
                            <span>Linhas:</span>
                            <Select value={rowsPerPage.toString()} onValueChange={(v) => { setRowsPerPage(parseInt(v)); setPage(0); }}>
                                <SelectTrigger className="h-8 w-[70px]"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {[5, 10, 25, 50].map(v => <SelectItem key={v} value={v.toString()}>{v}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex items-center gap-4">
                            <span className="text-sm text-gray-500">{page + 1}/{Math.max(1, Math.ceil(dataFiltered.length / rowsPerPage))}</span>
                            <Pagination>
                                <PaginationContent>
                                    <PaginationItem><PaginationPrevious onClick={() => setPage(Math.max(0, page - 1))} className="cursor-pointer" /></PaginationItem>
                                    <PaginationItem><PaginationNext onClick={() => setPage(Math.min(Math.ceil(dataFiltered.length / rowsPerPage) - 1, page + 1))} className="cursor-pointer" /></PaginationItem>
                                </PaginationContent>
                            </Pagination>
                        </div>
                    </div>
                </div>
            </div>

            {/* --- Wizard Modal --- */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className={MENSAGENS_AGENDADAS_DESIGN.dialog.content}>
                    <DialogHeader className={MENSAGENS_AGENDADAS_DESIGN.dialog.header}>
                        <DialogTitle className={MENSAGENS_AGENDADAS_DESIGN.dialog.title}>{isEdit ? 'Editar Agendamento' : 'Novo Agendamento'}</DialogTitle>
                    </DialogHeader>

                    {/* Stepper */}
                    <div className="bg-white border-b border-gray-100 py-6 px-10">
                        <div className="relative">
                            <div className="absolute top-[14px] left-0 w-full h-[2px] bg-gray-100 -z-0" />
                            <div className="absolute top-[14px] left-0 h-[2px] bg-[#7C3AED] transition-all duration-500 ease-in-out -z-0" style={{ width: `${((activeStep - 1) / (STEPS.length - 1)) * 100}%` }} />
                            <div className="flex justify-between relative z-10">
                                {STEPS.map((step) => {
                                    const isActive = step.id === activeStep;
                                    const isCompleted = step.id < activeStep;
                                    return (
                                        <div key={step.id} className="flex flex-col items-center gap-2 bg-white px-2">
                                            <div className={cn("w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all border-2", isActive ? "bg-[#7C3AED] border-[#7C3AED] text-white" : (isCompleted ? "bg-green-500 border-green-500 text-white" : "bg-white border-gray-200 text-gray-300"))}>
                                                {isCompleted ? <FaCheck size={10} /> : step.id}
                                            </div>
                                            <span className={cn("text-[10px] font-semibold uppercase tracking-wider", isActive ? "text-[#7C3AED]" : "text-gray-400")}>{step.label}</span>
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    </div>

                    <div className={MENSAGENS_AGENDADAS_DESIGN.dialog.body}>
                        {activeStep === 1 && (
                            <div className="space-y-6">
                                <div>
                                    <Label className={MENSAGENS_AGENDADAS_DESIGN.dialog.label}>Número de Telefone</Label>
                                    <Input
                                        placeholder="Ex: 5511999999999"
                                        value={numero}
                                        onChange={(e) => setNumero(e.target.value)}
                                        className="h-12 text-lg"
                                        disabled={isEdit}
                                    />
                                    <p className="text-xs text-gray-400 mt-1">Inclua o código do país e DDD (apenas números).</p>
                                </div>

                                {isMultisession && (
                                    <div>
                                        <Label className={MENSAGENS_AGENDADAS_DESIGN.dialog.label}>Canal de Envio</Label>
                                        <Select value={selectedClienteId} onValueChange={setSelectedClienteId} disabled={isEdit}>
                                            <SelectTrigger className="h-10"><SelectValue placeholder="Selecione o canal" /></SelectTrigger>
                                            <SelectContent>{listaClientesCanais.map(c => <SelectItem key={c.cliente_id} value={c.cliente_id}>{c.nome} ({c.platform})</SelectItem>)}</SelectContent>
                                        </Select>
                                    </div>
                                )}

                                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-100">
                                    <div>
                                        <p className="text-sm font-medium">Chat de Atendimento</p>
                                        <p className="text-xs text-gray-500">Direcionar resposta para atendimento humano?</p>
                                    </div>
                                    <Switch checked={isChatAtendimento} onCheckedChange={setIsChatAtendimento} />
                                </div>
                            </div>
                        )}

                        {activeStep === 2 && (
                            <div className="space-y-6">
                                <Label className={MENSAGENS_AGENDADAS_DESIGN.dialog.label}>Tipo de Mensagem</Label>
                                <div className="grid grid-cols-4 gap-2">
                                    {['text', 'file', 'audio'].map(type => (
                                        <div
                                            key={type}
                                            onClick={() => setContentType(type)}
                                            className={cn(
                                                "cursor-pointer border rounded-lg p-3 flex flex-col items-center gap-2 transition-all",
                                                contentType === type ? "border-[#7C3AED] bg-violet-50 text-[#7C3AED]" : "border-gray-200 hover:bg-gray-50 text-gray-500"
                                            )}
                                        >
                                            <Iconify icon={type === 'text' ? 'mdi:format-text' : type === 'file' ? 'mdi:file-document-outline' : 'mdi:microphone-outline'} width={24} />
                                            <span className="text-xs font-semibold">{type === 'text' ? 'Texto' : type === 'file' ? 'Arquivo' : 'Áudio'}</span>
                                        </div>
                                    ))}
                                </div>

                                {isWhatsAppBusiness && (
                                    <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-100">
                                        <div>
                                            <p className="text-sm font-medium text-green-900">Usar Template (WhatsApp)</p>
                                            <p className="text-xs text-green-700">Obrigatório para contatos fora da janela de 24h.</p>
                                        </div>
                                        <Switch checked={useTemplate} onCheckedChange={setUseTemplate} />
                                    </div>
                                )}

                                <div className="pt-2">
                                    {contentType === 'text' && (
                                        <Textarea
                                            value={body}
                                            onChange={(e) => setBody(e.target.value)}
                                            placeholder="Digite sua mensagem..."
                                            className="h-40 resize-none text-base bg-gray-50 border-gray-200 focus:bg-white transition-colors"
                                        />
                                    )}

                                    {contentType === 'file' && (
                                        <div className="border-2 border-dashed border-gray-200 rounded-lg p-8 text-center hover:bg-gray-50 transition-colors">
                                            <Input type="file" multiple className="hidden" id="file" onChange={(e) => setFiles(Array.from(e.target.files))} />
                                            <label htmlFor="file" className="cursor-pointer flex flex-col items-center gap-2">
                                                <Iconify icon="mdi:cloud-upload-outline" width={40} className="text-gray-300" />
                                                <span className="text-sm text-gray-600 font-medium">Clique para selecionar arquivos</span>
                                                {files.length > 0 && <span className="text-sm text-[#7C3AED]">{files.length} arquivo(s) selecionado(s)</span>}
                                            </label>
                                        </div>
                                    )}

                                    {contentType === 'audio' && (
                                        <div className="flex flex-col items-center justify-center p-8 bg-gray-50 rounded-lg border border-gray-200 gap-4">
                                            {!audioGravado ? (
                                                !recording ? (
                                                    <Button variant="outline" onClick={handleStartRecording} className="h-12 px-8 rounded-full border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700">
                                                        <BsMicFill className="mr-2" /> Gravar Áudio
                                                    </Button>
                                                ) : (
                                                    <Button variant="destructive" onClick={handleStopRecording} className="h-12 px-8 rounded-full animate-pulse">
                                                        Parar Gravação
                                                    </Button>
                                                )
                                            ) : (
                                                <div className="flex items-center gap-4">
                                                    <div className="flex items-center gap-2 text-green-600 font-medium bg-green-50 px-4 py-2 rounded-full border border-green-100">
                                                        <FaCheck /> Áudio Gravado
                                                    </div>
                                                    <Button variant="ghost" onClick={() => setAudioGravado(null)} className="text-red-500">Excluir</Button>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {useTemplate && (
                                        <div className="space-y-4 pt-4 border-t border-gray-100">
                                            <Select value={templateSelecionado?.templateId || templateSelecionado?._id} onValueChange={handleSelecionarTemplate}>
                                                <SelectTrigger className="bg-white"><SelectValue placeholder="Selecione um template" /></SelectTrigger>
                                                <SelectContent>
                                                    {templates.filter(t => t.status === 'APPROVED').map(t => (
                                                        <SelectItem key={t.templateId || t._id} value={t.templateId || t._id}>{t.name}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>

                                            {templateSelecionado && (
                                                <div className="space-y-4 animate-in fade-in slide-in-from-top-2">
                                                    <div className="p-3 bg-gray-50 rounded border text-sm text-gray-600 whitespace-pre-wrap">{templateSelecionado.conteudo}</div>
                                                    {Object.keys(templateVariaveis).length > 0 && (
                                                        <div className="space-y-2">
                                                            <Label className="text-xs uppercase text-gray-500">Variáveis</Label>
                                                            {Object.keys(templateVariaveis).map(key => (
                                                                <Input
                                                                    key={key}
                                                                    placeholder={`Valor para {{${key}}}`}
                                                                    value={templateVariaveis[key]}
                                                                    onChange={(e) => setTemplateVariaveis(prev => ({ ...prev, [key]: e.target.value }))}
                                                                />
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {activeStep === 3 && (
                            <div className="space-y-6">
                                <Label className={MENSAGENS_AGENDADAS_DESIGN.dialog.label}>Data e Hora do Envio</Label>
                                <div className="p-6 bg-indigo-50 border border-indigo-100 rounded-xl space-y-4">
                                    <div>
                                        <span className="text-xs font-semibold text-indigo-800 uppercase mb-1 block">Data</span>
                                        <Input type="date" value={dataAgendamento} onChange={e => setDataAgendamento(e.target.value)} className="bg-white border-indigo-200" />
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <span className="text-xs font-semibold text-indigo-800 uppercase mb-1 block">Hora</span>
                                            <Select value={hora} onValueChange={setHora}>
                                                <SelectTrigger className="bg-white border-indigo-200"><SelectValue placeholder="HH" /></SelectTrigger>
                                                <SelectContent>{Array.from({ length: 24 }, (_, i) => i).map(h => <SelectItem key={h} value={h.toString().padStart(2, '0')}>{h.toString().padStart(2, '0')}</SelectItem>)}</SelectContent>
                                            </Select>
                                        </div>
                                        <div>
                                            <span className="text-xs font-semibold text-indigo-800 uppercase mb-1 block">Minuto</span>
                                            <Select value={minuto} onValueChange={setMinuto}>
                                                <SelectTrigger className="bg-white border-indigo-200"><SelectValue placeholder="MM" /></SelectTrigger>
                                                <SelectContent>{['00', '10', '20', '30', '40', '50'].map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                                            </Select>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex gap-3 p-4 bg-yellow-50 border border-yellow-100 rounded-lg text-yellow-800 text-sm">
                                    <Iconify icon="mdi:information-outline" width={20} className="shrink-0 mt-0.5" />
                                    <div>
                                        <strong className="block mb-1">Confira os detalhes:</strong>
                                        Envio para <strong>{numero}</strong> em <strong>{fDateTimeSuffix(`${dataAgendamento}T${hora || '00'}:${minuto || '00'}:00`)}</strong>.
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <DialogFooter className={MENSAGENS_AGENDADAS_DESIGN.dialog.footer}>
                        <Button variant="outline" onClick={activeStep === 1 ? () => setDialogOpen(false) : handleBack}>
                            {activeStep === 1 ? 'Cancelar' : 'Voltar'}
                        </Button>
                        <Button
                            className="bg-[#7C3AED] hover:bg-[#6D28D9] min-w-[120px]"
                            onClick={activeStep === 3 ? onSubmit : handleNext}
                            disabled={loadingSubmit}
                        >
                            {loadingSubmit ? 'Salvando...' : (activeStep === 3 ? (isEdit ? 'Salvar Alterações' : 'Agendar Envio') : 'Próximo →')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation */}
            <Dialog open={modalConfirmarExclusao} onOpenChange={setModalConfirmarExclusao}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Confirmar Exclusão</DialogTitle>
                        <DialogDescription>Deseja realmente excluir este agendamento? Esta ação não pode ser desfeita.</DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setModalConfirmarExclusao(false)}>Cancelar</Button>
                        <Button variant="destructive" onClick={confirmDelete}>Excluir</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
